import CartSummary from './components/CartSummary';
import CartIndex from './components/CartIndex';
import ProductList from './components/ProductList';
import ProductTile from './components/ProductTile';
import { h, render } from 'preact';
import * as productActions from './actions/product';
import store from './store';
import { config } from '../shared/config';

function domEach(domNodeList, fn) {
  let len = domNodeList.length;

  for (let i = 0; i < len; i++) {
    fn(domNodeList[i], i);
  }
}

/**
 * Subscribers
 */
store.subscribe('shopper', (state) => {
  window.localStorage.setItem('_ic__shopper', JSON.stringify(state.shopper));

  if (state.shopper.cart.items.length === 0) {
    window.IncludeCart.api.cartHide();
  }
});
store.subscribe('local', (state) => {
  window.localStorage.setItem('_ic__local', JSON.stringify(state.local));
});

/**
 * afterInit (called in init)
 */
function afterInit() {
  const shopperState = window.localStorage.getItem('_ic__shopper');
  const localState = window.localStorage.getItem('_ic__local');

  if (shopperState) {
    store.set('shopper', JSON.parse(shopperState));
  }
  if (localState) {
    store.set('local', JSON.parse(localState));
  }
}

/**
 * IncludeCart public API
 */
const api = {
  assetsUrl: config.assetsUrl,
  baseUrl: config.apiUrl,
  stripeScriptUrl: config.stripeScriptUrl,

  /**
   * Init Includecart
   */
  init: function(config) {
    config.assetsUrl = api.assetsUrl;
    config.baseUrl = api.baseUrl;
    store.set('config', config);

    // Need to explicitly check for 'siteId' and 'stripePubKey' - both are REQUIRED!
    if (!config.siteId) {
      throw new Error('[IncludeCart] init() configuration parameter "siteId" is required, and was not supplied. Use "data-ic-siteId" attribute to define it on the IncludeCart <script> tag. Aborting!');
    }
    if (!config.stripePubKey) {
      throw new Error('[IncludeCart] init() configuration parameter "stripePubKey" is required, and was not supplied. Use "data-ic-stripePubKey" attribute to define it on the IncludeCart <script> tag. Aborting!');
    }

    window.IncludeCart = { api, store };

    api.loadCss(config);

    Promise.all([api.renderProductCollections(), api.renderProductTiles(), api.renderCartSummary()]).then(() => {
      afterInit();
    });
  },

  renderCartSummary() {
    const cartPlaceholders = document.querySelectorAll('.ic_cartSummary');

    // Render in placeholder if there is one
    if (cartPlaceholders.length > 0) {
      domEach(cartPlaceholders, (el) => {
        render(<CartSummary />, el);
      });

      return;
    }

    const el = document.createElement('div');

    el.className = '_ic__cartSummaryContainer';
    document.body.appendChild(el);

    // Append to page body if there is not one
    render(<CartSummary />, el);

    return Promise.resolve();
  },

  loadCss(config) {
    const cssSrc = config.assetsUrl + '/css/includecart.css';
    const hasCss = document.querySelector('link[href="' + cssSrc + '"]');

    if (hasCss) {
      return;
    }

    const cssTag = document.createElement('link');

    cssTag.href = cssSrc;
    cssTag.rel = 'stylesheet';
    cssTag.type = 'text/css';

    document.head.appendChild(cssTag);
  },

  /**
   * Load Stripe script and execute code after the script is loaded
   */
  loadStripe(callback) {
    const hasStripeScript = document.querySelector('script[src="' + api.stripeScriptUrl + '"]');

    if (hasStripeScript) {
      return callback && callback();
    }

    const script = document.createElement('script');

    script.src = api.stripeScriptUrl;
    script.onload = callback;

    document.head.appendChild(script);
  },

  cartShow() {
    const elClass = '_ic__cartModal-overlay';
    const body = document.body;

    api.loadStripe();
    api.cartHide();

    const el = document.createElement('div');

    el.className = '_ic ' + elClass + ' animated fadeInDown';
    body.appendChild(el);
    body.style.overflow = 'hidden';

    // Append to page body if there is not one
    render(<CartIndex />, el);

    return Promise.resolve();
  },

  cartIsOpen() {
    const elClass = '_ic__cartModal-overlay';
    return document.querySelector('.' + elClass);
  },

  cartHide() {
    const existingEl = api.cartIsOpen();

    if (existingEl) {
      existingEl.remove();
      document.body.style.overflow = 'scroll';
    }
  },

  cartToggle() {
    if (api.cartIsOpen()) {
      api.cartHide();
    } else {
      api.cartShow();
    }
  },

  /**
   * Product tile (single product display)
   */
  renderProductTiles() {
    const tilePlaceholders = document.querySelectorAll('.ic_productTile');
    const productIds = [];

    domEach(tilePlaceholders, (el) => {
      productIds.push(el.dataset.icProductid);
    });

    if (productIds.legth === 0) {
      console.warn('[IncludeCart] ic_productTile was used without data-ic-productId attribute (Stripe product id)');
      return;
    }

    return productActions.remote
      .fetchByIds(productIds)
      .then((products) => {
        domEach(tilePlaceholders, (el) => {
          const productId = el.dataset.icProductid;
          const product = products.find((p) => p.id === productId);

          if (!productId) {
            console.warn('[IncludeCart] ic_productTile was used without data-ic-productId attribute (Stripe product id)', el);
            return;
          }

          if (!product) {
            console.warn('[IncludeCart] No product found with id', productId, el);
            return;
          }

          el.innerHTML = '';
          render(<ProductTile product={product} />, el);
        });

        return products;
      })
      .catch(console.error);
  },

  /**
   * Product collection/group
   */
  renderProductCollections() {
    const productPlaceholders = document.querySelectorAll('.ic_productList');
    const promises = [];

    domEach(productPlaceholders, (el) => {
      promises.push(
        productActions.remote
          .fetchAll()
          .then((products) => {
            el.innerHTML = '';
            render(<ProductList products={products} />, el);
          })
          .catch(console.error)
      );
    });

    return Promise.all(promises);
  },
};

/**
 * Automatically detect and initialize includecart.js
 */
function autoInit() {
  let scriptTag = getScriptTag();

  if (scriptTag) {
    let siteId = scriptTag.dataset.icSiteid;
    let stripePubKey = scriptTag.dataset.icStripepubkey;

    api.init({ siteId, stripePubKey });
  } else {
    console.warn('No IncludeCart <script> tag detected! How can that be? How is this script even running right now?');
  }
}


/**
 * Get Includecart <script> tag and cache resulting found DOM node, if any
 *
 * @return {DOMNode|Boolean} <script> tag element found
 */
let _scriptTag = false;
function getScriptTag() {
  if (_scriptTag) {
    return _scriptTag;
  }

  // Look for IncludeCart.js script
  const scripts = document.querySelectorAll('script');

  domEach(scripts, (el) => {
    let src = el.getAttribute('src');

    if (src && src.toLowerCase().includes('includecart')) {
      _scriptTag = el;
    }
  });

  return _scriptTag;
}

// Run Initializers on page for IncludeCart
if (window._icinit && Array.isArray(window._icinit)) {
  // CUSTOM initializer
  window._icinit.map((fn) => fn(api));
} else {
  // Auto initializer (default/simple setup)
  autoInit();
}
